/**
 * Coffee Bean Clicker v2 - Browser Entry Point
 */

import * as PIXI from 'pixi.js';
import { createGameAPI } from '@app-games/api';
import { createGameCore } from './game/index.js';
import { GameView } from './view/GameView.js';
import { initI18n } from './i18n/index.js';
import { SoundManager } from './audio/SoundManager.js';
import { AdManager } from './monetization/index.js';

async function main() {
  // Initialize i18n
  initI18n();

  // Initialize audio
  SoundManager.init();

  // Initialize ads (auto-detect platform)
  AdManager.initialize('auto', { testMode: true });

  // Create PIXI application
  const app = new PIXI.Application({
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: 0x1A0E08,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    antialias: true,
  });
  document.getElementById('app')!.appendChild(app.view as HTMLCanvasElement);

  // Preload image assets before creating view
  await PIXI.Assets.load([
    { alias: 'bean-icon', src: 'assets/bean-icon.png' },
    { alias: 'farm-bg', src: 'assets/farm-bg.png' },
    { alias: 'golden-cherry', src: 'assets/golden-cherry.png' },
  ]);

  // Create headless game core
  const isDev = (import.meta as any).env?.DEV ?? false;
  const core = createGameCore(isDev);

  // Create GameAPI bridge (exposes to console in dev mode)
  createGameAPI(core, { exposeGlobal: isDev, debug: isDev });

  // Create view layer (subscribes to core state)
  const gameView = new GameView(app, core);

  // Game loop: tick the core + update view every frame
  let lastTime = performance.now();
  app.ticker.add(() => {
    const now = performance.now();
    const deltaTime = (now - lastTime) / 1000;
    lastTime = now;
    core.tick(deltaTime);
    gameView.onTick(deltaTime);
  });

  // Handle resize
  window.addEventListener('resize', () => {
    app.renderer.resize(window.innerWidth, window.innerHeight);
    gameView.resize();
  });
}

main().catch(console.error);
